import {
  useActionState,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import { BaseFieldProps } from '@/fo/ui/kit/field/base';
import { toastBase } from '@/fo/ui/kit/toast/base';

export type FormErrors = Record<string, string | string[] | undefined>;

export type FormState<T = Record<string, unknown>> = {
  data?: T;
  errors?: FormErrors;
  message?: string;
  status?: 'error' | 'success';
};

export type FormAction<T = Record<string, unknown>> = (
  prevState: FormState<T>,
  payload: FormData
) => Promise<FormState<T>>;

export type UseFormOptions = {
  /** Показывать message из state в тосте. false — вывод только через inline (например FormMessage) */
  displayMessageInToast?: boolean;
};

export const useForm = (
  formAction: FormAction,
  initialState: FormState = {},
  options: UseFormOptions = {}
) => {
  const { displayMessageInToast = true } = options;
  const [state, action, pending] = useActionState(formAction, initialState);
  const [errors, setErrors] = useState<typeof state.errors>({});
  const [isDirty, setIsDirty] = useState(false);
  const refState = useRef(state);
  refState.current = state;

  useEffect(() => {
    if (displayMessageInToast && !pending && state?.message && state?.status) {
      toastBase(state?.message, { status: state.status });
    }
  }, [displayMessageInToast, pending, state?.message, state?.status]);

  useEffect(() => {
    setErrors(state?.errors || {});
  }, [state?.errors]);

  const setFieldError = useCallback((field: string, value?: string) => {
    if (!field) return;

    const stateValue = refState.current.data?.[field];

    const notChanged =
      typeof value === 'boolean'
        ? value === (Boolean(stateValue) || stateValue === 'on')
        : value === stateValue;

    setErrors((s) => ({
      ...s,
      [field]: notChanged ? s?.[field] : undefined
    }));
  }, []);

  const handleFieldChange = useCallback(
    (name: string, value: unknown) => {
      setIsDirty(true);
      setFieldError(name, value as string);
    },
    [setFieldError]
  );

  const getFieldProps = useCallback(
    (name: string): BaseFieldProps => {
      const fieldsError: FormErrors = errors || {};
      return {
        name,
        error: !!fieldsError[name],
        helperText: Array.isArray(fieldsError[name])
          ? fieldsError[name][0]
          : fieldsError[name],
        onChange: (value) => handleFieldChange(name, value)
      };
    },
    [errors, handleFieldChange]
  );

  return {
    state,
    action,
    pending,
    errors,
    isDirty,
    getFieldProps,
    handleFieldChange
  };
};
